import Paper from '@/components/Paper'
import { useTranslation } from 'react-i18next'
import { IncomeStats, StatItem } from '../../types'
import { formatCurrency, formatNumeral } from '@/utils/function'
import {
    Users,
    Package,
    Server,
    Building2,
    TrendingUp,
    FileText,
} from 'lucide-react'
import { Typogaphy } from 'vnetwork-ui'

interface SummaryProps {
    stats: IncomeStats | null | undefined
}

interface TopListProps {
    title: string
    items: StatItem[]
    icon: React.ReactNode
    bgClass: string
    iconBgClass: string
}

const TopList = ({
    title,
    items,
    icon,
    bgClass,
    iconBgClass,
}: TopListProps) => {
    if (!items || items.length === 0) return null

    return (
        <div className={`rounded-sm ${bgClass} p-4`}>
            <div className="flex items-center gap-2 mb-2.5">
                <div
                    className={`w-5 h-5 rounded-sm ${iconBgClass} flex items-center justify-center`}
                >
                    {icon}
                </div>
                <Typogaphy className="text-xs font-medium opacity-80 uppercase tracking-wide">
                    {title}
                </Typogaphy>
            </div>
            <div className="space-y-1">
                {items.map((item) => (
                    <div
                        key={`${item.entity_id}-${item.rank}`}
                        className="flex items-center gap-2 py-0.5 text-xs"
                    >
                        <Typogaphy className="w-5 h-5 rounded-full bg-white/70 flex items-center justify-center text-xs text-gray-500 shrink-0">
                            {item.rank}
                        </Typogaphy>
                        <Typogaphy
                            className="flex-1 truncate text-gray-700 text-xs"
                            title={item.entity_name}
                        >
                            {item.entity_name}
                        </Typogaphy>
                        <Typogaphy className="text-right font-medium text-gray-800 text-xs whitespace-nowrap">
                            {formatCurrency(item.total_amount)}
                        </Typogaphy>
                    </div>
                ))}
            </div>
        </div>
    )
}

const Summary = ({ stats }: SummaryProps) => {
    const { t } = useTranslation(['report'])

    if (!stats) return null

    return (
        <Paper className="p-4 space-y-2">
            {/* Stat cards + Top lists in single row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-2">
                {/* Total Revenue */}
                <div className="rounded-sm bg-gradient-to-br from-blue-500 to-blue-600 p-4 text-white">
                    <div className="flex items-center gap-1.5 mb-1">
                        <TrendingUp className="w-3.5 h-3.5 opacity-80" />
                        <Typogaphy className="text-xs font-medium opacity-80 uppercase tracking-wide">
                            {t('summary.total_amount')}
                        </Typogaphy>
                    </div>
                    <Typogaphy className="text-lg">
                        {formatCurrency(stats.total_amount)}
                    </Typogaphy>
                </div>

                {/* Total Invoice Items */}
                <div className="rounded-sm bg-gradient-to-br from-violet-500 to-violet-600 p-4 text-white">
                    <div className="flex items-center gap-1.5 mb-1">
                        <FileText className="w-3.5 h-3.5 opacity-80" />
                        <Typogaphy className="text-xs font-medium opacity-80 uppercase tracking-wide">
                            {t('summary.total_invoice_items')}
                        </Typogaphy>
                    </div>
                    <Typogaphy className="text-lg">
                        {formatNumeral(stats.total_invoice_items, 0)}
                    </Typogaphy>
                </div>

                {/* Top Employees */}
                <TopList
                    title={t('summary.top_employees')}
                    items={stats.top_employees}
                    icon={<Users className="w-3.5 h-3.5 text-amber-600" />}
                    bgClass="bg-amber-50"
                    iconBgClass="bg-amber-100"
                />

                {/* Top Product Groups */}
                <TopList
                    title={t('summary.top_product_groups')}
                    items={stats.top_product_groups}
                    icon={<Package className="w-3.5 h-3.5 text-indigo-600" />}
                    bgClass="bg-indigo-50"
                    iconBgClass="bg-indigo-100"
                />

                {/* Top Services */}
                <TopList
                    title={t('summary.top_services')}
                    items={stats.top_services}
                    icon={<Server className="w-3.5 h-3.5 text-teal-600" />}
                    bgClass="bg-teal-50"
                    iconBgClass="bg-teal-100"
                />

                {/* Top Customers */}
                <TopList
                    title={t('summary.top_customers')}
                    items={stats.top_customers}
                    icon={<Building2 className="w-3.5 h-3.5 text-rose-600" />}
                    bgClass="bg-rose-50"
                    iconBgClass="bg-rose-100"
                />
            </div>
        </Paper>
    )
}

export default Summary
